import { FC } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer,
} from 'recharts'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

const DEPT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316']
const RANK_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1', '#84CC16']

const deptMeta: CardMeta = {
  description: 'Distribution of crew members grouped by department (Deck, Engine, Galley, etc.).',
  endpoint: 'GET /crew/all → aggregated by department',
  logic: 'Crew list grouped by department field; shows top departments with crew counts.',
}

const rankMeta: CardMeta = {
  description: 'Crew members grouped by rank, showing relative proportion.',
  endpoint: 'GET /crew/all → aggregated by rankId, resolved via GET /ranks/drop-down',
  logic: 'Groups crew by rankId, looks up rank name from ranks list. Progress bar width = count / max(count).',
}

interface Props {
  deptChartData: { department: string; count: number }[]
  rankChartData: { rankName: string; count: number }[]
}

const CrewComposition: FC<Props> = ({ deptChartData, rankChartData }) => {
  const maxCount = Math.max(...rankChartData.map(r => r.count), 1)

  return (
    <>
      {/* Left: BarChart - Crew by Department */}
      <div className='xl:col-span-7 flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-4'>
          <h6 className='text-sm font-bold text-slate-900'>Crew by Department</h6>
          <CardInfoButton meta={deptMeta} />
        </div>
        <div className='flex-1 min-h-0'>
          <ResponsiveContainer width='100%' height={280}>
            <BarChart data={deptChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
              <XAxis dataKey='department' tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RTooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }}
                formatter={(value: any) => [value, 'Crew']}
              />
              <Bar dataKey='count' radius={[6, 6, 0, 0]} maxBarSize={48} fill='#3B82F6' />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {deptChartData.length === 0 && (
          <div className='text-center text-slate-400 py-8 text-xs'>No department data available</div>
        )}
      </div>

      {/* Right: Crew by Rank */}
      <div className='xl:col-span-5 flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-4'>
          <h6 className='text-sm font-bold text-slate-900'>Crew by Rank</h6>
          <CardInfoButton meta={rankMeta} />
        </div>
        <div className='flex-1 space-y-3'>
          {rankChartData.slice(0, 8).map((item, i) => {
            const pct = (item.count / maxCount) * 100
            return (
              <div key={i}>
                <div className='flex justify-between items-center mb-1'>
                  <span className='text-[10px] text-slate-700 font-semibold'>{item.rankName}</span>
                  <span className='text-[10px] text-slate-400'>{item.count}</span>
                </div>
                <div className='h-1.5 rounded bg-slate-100 overflow-hidden'>
                  <div className='h-full rounded transition-all duration-500' style={{ width: `${pct}%`, background: RANK_COLORS[i % RANK_COLORS.length] }} />
                </div>
              </div>
            )
          })}
          {rankChartData.length === 0 && (
            <div className='text-center text-slate-400 py-8 text-xs'>No rank data available</div>
          )}
        </div>
      </div>
    </>
  )
}

export default CrewComposition
